import {
  // Navigation & UI
  faComments,
  faFileLines,
  faMicrochip,
  faGear,
  faSidebar,
  faSidebarFlip,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faXmark,
  faBars,
  faHouse,

  // User & Profile
  faCircleUser,
  faArrowRightFromBracket,
  faBookOpen,
  faBooks,
  faMask,
  faUsers,
  faBriefcase,
  faBrain,

  // Actions
  faPen,
  faCalendarLinesPen,
  faTrash,
  faCheck,
  faCheckDouble,
  faClipboard,
  faClipboardCheck,
  faArrowsRotate,
  faArrowRightArrowLeft,
  faPlus,
  faArrowUp,
  faArrowDownBigSmall,
  faArrowUpRightFromSquare,
  faMagnifyingGlass,
  faCircleStop,
  faList,
  faListTree,
  faSquareList,
  faFilePen,

  // Notes
  faNotebook,
  faMemoPad,

  // Cloud & Storage
  faCloud,

  // Media & Files
  faImage,
  faFile,
  faMicrophone,
  faFlask,
  faBookSparkles,
  faFolder,
  faFolderPlus,
  faDiagramProject,
  faFolderTree,
  faCode,
  faCirclePlay,
  faCirclePause,
  faBackwardStep,
  faForwardStep,
  faVideo,
  faPenFancy,
  faCirclesOverlap,
  faCodeBranch,
  faLink,

  // Status & Alerts
  faTriangleExclamation,
  faEye,
  faHighlighterLine,
  faGripVertical,

  // Chat & Communication
  faComment,
  faMessage,

  // Settings & Additional
  faGlobe,
  faShieldCheck,
  faLock,
  faPalette,
  faPuzzlePiece,
  faCommentPlus,
  faMessagePlus,

  // Admin
  faBan,
  faDollarSign,
  faMoneyCheckDollar,
  faSunBright,
  faQuoteLeft,
  faEnvelope,
  faTerminal,
  faGears,
  faRobot,
  faCalculator,
  faBolt,
  faChartPie,
  faCircleDollar,
  faChartBar,
  faChartLine,
  faPiggyBank,
  faUmbrellaBeach,

  // Events & Calendar
  faBell,
  faCalendar,
  faCalendarDay,
  faGift,
  faCake,
  faHeart,
  faStar,
  faLightbulb,
  faLightbulbExclamation,
  faLightbulbSlash,
  faSparkles,
  faClock,
  faEdit as faEdit2,
  faTrash as faTrash2,
  faCircleCheck,
  faCircleInfo,

  // Menu & Food
  faUtensils,
  faAppleWhole,
  faCar,

  // Reading Queue
  faBoxArchive,
  faBookBookmark,
  faBookUser,
  faClockRotateLeft,
  faAlarmClock,
  faAlarmSnooze,
  faTag,
  faTags,

  faCircleXmark,

  // Email Actions
  faReply,
  faReplyAll,
  faShare,
  faPaperclip,
  faInboxIn,
  faPaperPlaneTop,
  faThumbtack,
  faDownload,
  faTableCellsLarge,
  faSliders,
  faEllipsisVertical,
  faEllipsis,
  faEllipsisStroke,
  faUpRightAndDownLeftFromCenter,
  faBullseye,
  faScissors,
  faDownLeftAndUpRightToCenter,

  // Contacts
  faPhone,
  faPenToSquare,
  faFileExport,
  faFileImport,
  faBoxOpen,

  // Study
  faLampDesk,
  faDesk,
  faSquareKanban,

  // Explorer polish
  faPenLine,
  faClone,
  faCamera,
  faBarcodeRead,
  faCameraMovie,
  faFilePlus,
  faFolderMagnifyingGlass,
  faTableColumnsAddAfter,
} from '@fortawesome/pro-duotone-svg-icons';

import { createIcon } from './icon-utils';

import type { IconDefinition, IconPrefix, IconName } from '@fortawesome/fontawesome-svg-core';

/** Hand-crafted solid star icon definition — avoids adding @fortawesome/pro-solid-svg-icons. */
const faStarSolid: IconDefinition = {
  prefix: 'fas' as IconPrefix,
  iconName: 'star' as IconName,
  icon: [
    576,
    512,
    [],
    'f005',
    'M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329l-24.6 145.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3L288.1 439.8l128.2 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329l104.2-103.1c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z',
  ],
};

// Export all icons with consistent theming support
export const Icons = {
  // Navigation & UI
  Home: createIcon(faHouse),
  Chat: createIcon(faComments),
  Conversations: createIcon(faFileLines),
  Memories: createIcon(faMicrochip),
  Settings: createIcon(faGear),
  Sidebar: createIcon(faSidebar),
  SidebarFlip: createIcon(faSidebarFlip),
  ChevronRight: createIcon(faChevronRight),
  ChevronLeft: createIcon(faChevronLeft),
  ChevronDown: createIcon(faChevronDown),
  ChevronUp: createIcon(faChevronUp),
  Close: createIcon(faXmark),
  Menu: createIcon(faBars),

  // User & Profile
  User: createIcon(faCircleUser),
  UserCircle: createIcon(faCircleUser), // Alias for User
  Logout: createIcon(faArrowRightFromBracket),
  Book: createIcon(faBookOpen),
  Books: createIcon(faBooks),
  Mask: createIcon(faMask),
  UserGroup: createIcon(faUsers),
  Briefcase: createIcon(faBriefcase),
  Brain: createIcon(faBrain),

  // Actions
  Edit: createIcon(faPen),
  CalendarLinesPen: createIcon(faCalendarLinesPen),
  EditSolid: createIcon(faPen),
  Delete: createIcon(faTrash),
  Trash: createIcon(faTrash), // Alias for Delete
  Check: createIcon(faCheck),
  CheckDouble: createIcon(faCheckDouble),
  Copy: createIcon(faClipboard),
  ClipboardCheck: createIcon(faClipboardCheck),
  Refresh: createIcon(faArrowsRotate),
  ArrowRightArrowLeft: createIcon(faArrowRightArrowLeft),
  ArrowDownBigSmall: createIcon(faArrowDownBigSmall),
  Add: createIcon(faPlus),
  Send: createIcon(faArrowUp),
  ExternalLink: createIcon(faArrowUpRightFromSquare),
  Search: createIcon(faMagnifyingGlass),
  CircleStop: createIcon(faCircleStop),
  List: createIcon(faList),
  // Document outline / heading tree.
  ListTree: createIcon(faListTree),
  SquareList: createIcon(faSquareList),
  FilePen: createIcon(faFilePen),

  // Cloud & Storage
  Cloud: createIcon(faCloud),

  // Media & Files
  Photo: createIcon(faImage),
  Document: createIcon(faFile),
  Audio: createIcon(faMicrophone),
  Flask: createIcon(faFlask),
  BookSparkles: createIcon(faBookSparkles),
  Code: createIcon(faCode),
  Play: createIcon(faCirclePlay),
  Pause: createIcon(faCirclePause),
  // Read aloud's player bar: previous / next paragraph.
  BackwardStep: createIcon(faBackwardStep),
  ForwardStep: createIcon(faForwardStep),
  Video: createIcon(faVideo),
  PenFancy: createIcon(faPenFancy),
  CirclesOverlap: createIcon(faCirclesOverlap),

  // Status & Alerts
  Warning: createIcon(faTriangleExclamation),
  Eye: createIcon(faEye),
  HighlighterLine: createIcon(faHighlighterLine),
  GripVertical: createIcon(faGripVertical),

  // Chat & Communication
  ChatBubble: createIcon(faComment),
  CommentPlus: createIcon(faCommentPlus),
  MessageSquare: createIcon(faMessage),
  MessageSquarePlus: createIcon(faMessagePlus),

  // Settings & Additional
  Globe: createIcon(faGlobe),
  Shield: createIcon(faShieldCheck),
  Lock: createIcon(faLock),
  Palette: createIcon(faPalette),
  Puzzle: createIcon(faPuzzlePiece),

  // Study
  LampDesk: createIcon(faLampDesk),
  Desk: createIcon(faDesk),
  SquareKanban: createIcon(faSquareKanban),

  // Explorer polish
  PenLine: createIcon(faPenLine),
  Clone: createIcon(faClone),
  Camera: createIcon(faCamera),
  BarcodeRead: createIcon(faBarcodeRead),
  CameraMovie: createIcon(faCameraMovie),
  FilePlus: createIcon(faFilePlus),
  FolderMagnifyingGlass: createIcon(faFolderMagnifyingGlass),
  TableColumnsAddAfter: createIcon(faTableColumnsAddAfter),

  // Admin
  DollarSign: createIcon(faDollarSign),
  MoneyCheckDollar: createIcon(faMoneyCheckDollar),
  Sun: createIcon(faSunBright),
  Quote: createIcon(faQuoteLeft),
  Mail: createIcon(faEnvelope),
  Terminal: createIcon(faTerminal),
  Gears: createIcon(faGears),
  Robot: createIcon(faRobot),
  Calculator: createIcon(faCalculator),
  Zap: createIcon(faBolt),
  ChartPie: createIcon(faChartPie),
  CircleDollar: createIcon(faCircleDollar),
  Activity: createIcon(faChartLine),

  // Events & Calendar
  Bell: createIcon(faBell),
  Calendar: createIcon(faCalendar),
  CalendarDay: createIcon(faCalendarDay),
  Gift: createIcon(faGift),
  Cake: createIcon(faCake),
  Heart: createIcon(faHeart),
  Star: createIcon(faStar),
  StarSolid: createIcon(faStarSolid),
  Mute: createIcon(faCircleXmark),
  Lightbulb: createIcon(faLightbulb),
  LightbulbExclamation: createIcon(faLightbulbExclamation),
  LightbulbSlash: createIcon(faLightbulbSlash),
  Sparkles: createIcon(faSparkles),
  Clock: createIcon(faClock),
  // Version history. Distinct from `Clock` (recently-changed) and from
  // `ReadLater`, which is the same glyph flipped for the reading queue.
  History: createIcon(faClockRotateLeft),
  Edit2: createIcon(faEdit2),
  Trash2: createIcon(faTrash2),
  CheckCircle: createIcon(faCircleCheck),
  Info: createIcon(faCircleInfo),
  X: createIcon(faXmark), // Alias for Close
  Plus: createIcon(faPlus), // Alias for Add
  RefreshCw: createIcon(faArrowsRotate), // Alias for Refresh

  // Financial
  Ban: createIcon(faBan),
  ChartBar: createIcon(faChartBar),
  PiggyBank: createIcon(faPiggyBank),
  UmbrellaBeach: createIcon(faUmbrellaBeach),

  // Menu & Food
  Utensils: createIcon(faUtensils),
  AppleWhole: createIcon(faAppleWhole),
  Car: createIcon(faCar),

  // Split & Links
  Split: createIcon(faCodeBranch),
  Link: createIcon(faLink),

  // Notes
  NotebookPen: createIcon(faNotebook),
  MemoPad: createIcon(faMemoPad),

  // Reading Queue
  ReadLater: createIcon(faClockRotateLeft, { flip: 'horizontal' }),
  Archive: createIcon(faBoxArchive),
  Bookmark: createIcon(faBookBookmark),
  BookUser: createIcon(faBookUser),
  AlarmClock: createIcon(faAlarmClock),
  AlarmSnooze: createIcon(faAlarmSnooze),
  FileText: createIcon(faFileLines),
  Folder: createIcon(faFolder),
  FolderPlus: createIcon(faFolderPlus),
  DiagramProject: createIcon(faDiagramProject),
  FolderTree: createIcon(faFolderTree),
  Tag: createIcon(faTag),
  Tags: createIcon(faTags),

  // Contacts
  Phone: createIcon(faPhone),
  PenToSquare: createIcon(faPenToSquare),
  FileExport: createIcon(faFileExport),
  FileImport: createIcon(faFileImport),
  Unarchive: createIcon(faBoxOpen),

  // Email Actions
  Reply: createIcon(faReply),
  ReplyAll: createIcon(faReplyAll),
  Forward: createIcon(faShare),
  Attachment: createIcon(faPaperclip),
  InboxIn: createIcon(faInboxIn),
  PaperPlaneTop: createIcon(faPaperPlaneTop),
  Pin: createIcon(faThumbtack),
  Download: createIcon(faDownload),
  GridView: createIcon(faTableCellsLarge),
  Filter: createIcon(faSliders),
  MoreVertical: createIcon(faEllipsisVertical),
  More: createIcon(faEllipsis),
  EllipsisStroke: createIcon(faEllipsisStroke),

  // Panel expand/collapse
  Bullseye: createIcon(faBullseye),
  Scissors: createIcon(faScissors),
  Expand: createIcon(faUpRightAndDownLeftFromCenter),
  Compress: createIcon(faDownLeftAndUpRightToCenter),
};
